import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { DEV_VIEW_MODES, RateChangesService } from './rate-changes.service';
import { ChangeTabKey, CHANGE_TAB_KEYS } from './rate-changes.models';
import { SessionContextService } from '../../core/services/session-context.service';
import { StateAvailabilityGridComponent } from '../../shared/state-availability/state-availability-grid.component';

/**
 * Rail for /changes.
 *
 * Mounted by RailHostComponent from the route's `data: { rail: ... }` when the
 * horizontal shell is active. Without one the shell still reserves the 220px
 * column and it renders empty, which is why this exists.
 *
 * Shares state with UpcomingChangesComponent through RateChangesService — the
 * two are mounted by different hosts and cannot exchange inputs.
 *
 * Per the rail-is-a-summary rule this SUMMARISES the applied state and offers
 * the coarse controls (window, product type). It is not a second filter UI:
 * anything finer belongs in the page body.
 */
@Component({
  selector: 'app-changes-rail',
  standalone: true,
  imports: [CommonModule, StateAvailabilityGridComponent],
  template: `
    <!-- VIEW now opens with the two arrangements that ship: Grid and Calendar.
         The rest — Grid 0, Timeline, Carrier — stay in the DEV block at the
         foot of this rail. -->

    <!-- NOT ON GRID2. The forward window and the look back are two halves of a
         split the grid needs because it opens on a NOW divider. grid2 is one
         chronology, newest first, so a second depth control beside the look back
         reads as two answers to the same question. -->
    @if (!isGrid2()) {
      <div class="rail-section">
        <div class="rail-h">Window</div>
        <div class="pills">
          @for (w of windows; track w) {
            <button class="pill" [class.on]="svc.windowDays() === w"
                    (click)="svc.setWindow(w)">{{ w }}d</button>
          }
        </div>
        <div class="note">
          @if (isCalendar()) {
            How far AHEAD to load. The calendar pages past this freely and says
            so when it does.
          } @else {
            Filed ahead — every row above the NOW divider is a rate period with a
            future start date.
          }
        </div>
      </div>
    }

    <!-- TWO GROUPS, and the split is the one that was already true in the code
         but never said on screen: FILTERS narrow which rows exist, VIEW changes
         how the same rows are arranged. Reset belongs to the first only - it has
         nothing to do with grain or spine, and offering it over both would imply
         it did. -->
    <div class="rail-group">
      <div class="group-head">
        <span class="group-title">Filters</span>
        @if (svc.anyFilterOn()) {
          <button class="reset-link" (click)="svc.resetFilters()">Reset</button>
        }
      </div>
    </div>

    <!-- SEPARATE FROM THE FORWARD WINDOW, deliberately. "What is filed" and
         "what has landed" are asked at different depths, and widening one to a
         year should not drag a year of the other with it. This was a fixed 30
         while the grid rendered the forward book alone - nobody could see far
         enough back for it to matter. -->
    <div class="rail-section">
      <div class="rail-h">Look back</div>
      <div class="pills">
        @for (w of pastWindows; track w) {
          <button class="pill" [class.on]="svc.pastDays() === w"
                  (click)="svc.setPastWindow(w)">{{ w }}d</button>
        }
      </div>
      <div class="note">
        @if (isCalendar()) {
          How far BACK to load. Earlier months draw as empty, not as quiet.
        } @else {
          Below the NOW divider — rate periods already in force.
        }
      </div>
    </div>

    <div class="rail-section">
      <div class="rail-h">Product type</div>
      <div class="cats">
        @for (t of types; track t.key) {
          @if (svc.canSeeType(t.key)) {
            <div class="cat" [class.on]="svc.isTypeOn(t.key)" (click)="svc.toggleType(t.key)">
              <span>{{ t.label }}</span>
              <span class="ct">{{ count(t.key) }}</span>
            </div>
          }
        }
      </div>
      <div class="note">RILA and VA share one rate table upstream but are listed apart here: a RILA change moves the cap, a VA change the participation rate.</div>
    </div>

    <!-- GRID2 ONLY. Carrier and product as EXACT pickers rather than more free
         text: the header search already does substring matching across
         everything, so a second fuzzy box would be the same tool twice. These
         answer "this carrier, this product" precisely.

         Product narrows to the chosen carrier. Unfiltered it is ~2,000 names,
         which is not a list anyone reads in a 220px rail. -->
    @if (isGrid2()) {
      <div class="rail-section">
        <!-- Not "Filters" - that is the GROUP title now, and a section repeating
             it read as a filter section inside a filters section. -->
        <div class="rail-h">Carrier &amp; product</div>
        <select class="sel" [value]="svc.companyFilter()"
                (change)="pickCompany(asValue($event))">
          <option value="">All carriers</option>
          @for (c of svc.companyOptions(); track c) {
            <option [value]="c">{{ c }}</option>
          }
        </select>
        <select class="sel" [value]="svc.productFilter()"
                (change)="svc.productFilter.set(asValue($event))">
          <option value="">{{ svc.companyFilter() ? 'All of this carrier' : 'All products' }}</option>
          @for (pr of svc.productOptions(); track pr) {
            <option [value]="pr">{{ pr }}</option>
          }
        </select>
        @if (svc.companyFilter() || svc.productFilter()) {
          <button class="clear" (click)="clearFilters()">Clear</button>
        }
        <div class="note">Exact match. The header search is the fuzzy one.</div>
      </div>
    }

    <!-- STATE AVAILABILITY. The same control /rates and /products carry, so a
         reader who has picked states on one page finds the identical grid here.
         NOT gated to grid2, unlike carrier and product above: state is a
         property of the product, so it narrows the calendar as sensibly as it
         narrows the grid, and it clears with Reset like the other filters.

         Availability is joined from the product catalogs — the changes feed has
         no state column — which is why the note appears until they land. -->
    <div class="rail-section">
      <div class="rail-h">State availability</div>
      <!-- The bleed makes this grid the same width as the one on /rates and
           /products, to the pixel. .rail-section pads 14 a side; /rates reaches
           its grid through a body padded 8 left / 2 right, so -6/-12 lands on
           those same two numbers and the control's own 12/12 does the rest.
           Only the grid bleeds — the heading and note keep the section's
           padding and stay aligned with every other heading on this rail. -->
      <div class="state-grid-bleed">
        <app-state-availability-grid
          [selected]="svc.selectedStates()"
          (stateToggle)="svc.toggleState($event)" />
      </div>
      @if (svc.selectedStates().length > 0) {
        <button class="clear" (click)="svc.selectedStates.set([])">Clear states</button>
      }
      @if (!svc.stateDataReady()) {
        <div class="note">Loading availability from the product catalogue…</div>
      } @else {
        <div class="note">Products sold in ANY chosen state. Empty means every state.</div>
      }
    </div>

    <div class="rail-group">
      <div class="group-head"><span class="group-title">View</span></div>
    </div>

    <!-- THE TWO ARRANGEMENTS THAT SHIP, and the first thing under VIEW because
         it decides what every control below it means: the grid's zone, grain and
         spine choices do not exist on a calendar, and the calendar's own level
         control lives on the calendar. Two pills rather than a radio pair —
         this is the same question the Window and Look back pills ask, and a
         reader who has just used those reads a third set of pills without
         stopping.

         NOT DEV-GATED. grid2 has been the landing view since 2026-08-26, and
         while the only route between arrangements was the admin-only DEV block a
         non-admin reader had no way to reach the calendar at all. -->
    <div class="rail-section">
      <div class="rail-h">Arrangement</div>
      <div class="pills">
        <button class="pill" [class.on]="isGrid2()"
                (click)="svc.viewMode.set('grid2')">Grid</button>
        <button class="pill" [class.on]="isCalendar()"
                (click)="svc.viewMode.set('calendar')">Calendar</button>
      </div>
      <div class="note">{{ arrangementNote() }}</div>
    </div>

    @if (isGrid2()) {

      <!-- Rows by zone. NOT the same question as the look back: that sets how
           far back to LOAD, this sets which side of today to SHOW. -->
      <div class="rail-section">
        <div class="rail-h">Show</div>
        <div class="pills">
          @for (z of zones; track z.key) {
            <button class="pill" [class.on]="svc.isZoneOn(z.key)"
                    (click)="svc.toggleZone(z.key)">{{ z.label }}</button>
          }
        </div>
        <div class="note">The last one on cannot be switched off — an empty grid is not a filter state.</div>
      </div>

      <!-- GRAIN, beside the spine control because both restructure the row list
           rather than narrowing it. Neither belongs among the filters: Clear has
           nothing to do with either. -->
      <div class="rail-section">
        <div class="rail-h">Rows per product</div>
        <!-- RADIOS, not pills. Two adjacent pills leave the reader working out
             whether they are a toggle pair or two independent switches; a radio
             list says "one of these" before it is clicked. -->
        <div class="radio-list">
          <button class="choice" [class.on]="svc.grain() === 'row'"
                  (click)="svc.grain.set('row')">
            <span class="dot"></span><span>Every row</span></button>
          <button class="choice" [class.on]="svc.grain() === 'product'"
                  (click)="svc.grain.set('product')">
            <span class="dot"></span><span>One per product</span></button>
        </div>
        <div class="note">Folded, a product's rate rows for a day become one row you can open. Nothing is hidden.</div>
      </div>

      <div class="rail-section">
        <div class="rail-h">Timeline</div>
        <div class="radio-list">
          <button class="choice" [class.on]="svc.continuousSpine()"
                  (click)="svc.continuousSpine.set(true)">
            <span class="dot"></span><span>Continuous dates</span></button>
          <button class="choice" [class.on]="!svc.continuousSpine()"
                  (click)="svc.continuousSpine.set(false)">
            <span class="dot"></span><span>Change dates only</span></button>
        </div>
        <div class="note">Continuous accounts for every day in the window, collapsing runs of empty days to one row each.</div>
      </div>
    }

    <!-- Shelf. Three states, and the third is not a control:
         no shelf at all -> nothing to offer; access 'shelf' on everything the
         user can reach -> the scope is forced, so a toggle could only be a
         no-op and this states the fact instead; otherwise -> the real switch,
         which for a d_shelf user is the same shelfMode the rest of the app
         reads. -->
    @if (svc.shelfForced()) {
      <div class="rail-section">
        <div class="rail-h">Shelf</div>
        <div class="cat locked"><span><span class="star">&#9733;</span> Shelf only</span></div>
        <div class="note">Your account is scoped to the company shelf, so every change listed is on it.</div>
      </div>
    } @else if (svc.hasAnyShelf()) {
      <div class="rail-section">
        <div class="rail-h">Shelf</div>
        <div class="cats">
          <div class="cat" [class.on]="svc.shelfOnly()" (click)="svc.shelfOnly.set(!svc.shelfOnly())">
            <span><span class="star">&#9733;</span> On my shelf</span>
            <span class="ct">{{ svc.shelfCount() }}</span>
          </div>
        </div>
        <div class="note">Starred rows are on your company shelf. This narrows the page to them.</div>
      </div>
    }

    <!-- Both off on grid2. "Scheduled" counts the forward half alone, which on a
         view showing both halves as one chronology is a number about a window
         grid2 does not have; and the rates-grid jump is the shipping grid's own
         next step, not this one's. -->
    @if (!isGrid2()) {
      <div class="rail-section">
        <div class="rail-h">Showing</div>
        <div class="stat"><span class="v">{{ svc.upcoming().length }}</span> scheduled</div>
        <div class="stat sub">{{ soonest() }}</div>
      </div>

      <div class="rail-section">
        <button class="rail-btn" (click)="goToRates()">Open the rates grid →</button>
        <div class="note">The grid shows a pending change on the product row. This page is the only place a cross-product-type day appears.</div>
      </div>
    }

    <!-- WHAT IS LEFT HERE after Grid and Calendar moved up to VIEW: three
         readings that are still experiments rather than arrangements anyone has
         asked to ship. They read the rows in ways the two above cannot —
         sortable columns and the forward window (Grid 0), order and gaps
         (Timeline), per-carrier (Carrier). Each is also reachable as ?view=KEY
         for a linkable route in, which does NOT require admin.

         The gate is no longer load-bearing for reachability: the pair a reader
         needs is in VIEW above, un-gated, so hiding this block now costs an
         admin a comparison rather than costing a customer their way back.

         NO BACKTICKS IN THIS COMMENT. It sits inside a template literal, so one
         terminates the literal and the build fails with a pile of
         unrelated-looking NG1002/TS2349 errors pointing at the decorator. -->
    @if (session.isBeaconAdmin()) {
      <div class="rail-section dev">
        <div class="rail-h">More views <span class="dev-tag">DEV</span></div>
        <div class="pills">
          @for (v of devViews; track v.key) {
            <button class="pill" [class.on]="svc.viewMode() === v.key"
                    (click)="svc.viewMode.set(v.key)">{{ v.label }}</button>
          }
        </div>
        <div class="note">{{ viewNote() }}</div>
      </div>
    }
  `,
  styles: [`
    :host { display: block; padding-bottom: 16px; }
    .rail-section { padding: 8px 14px; }
    /* See the template — cancels enough of .rail-section's 14px so the state
       grid sits at 8 left / 2 right like the picker body on the other two
       rails. Recompute if .rail-section's padding moves. */
    .state-grid-bleed { margin-left: -6px; margin-right: -12px; }
    .rail-section + .rail-section { border-top: 1px solid var(--border-light); }
    .rail-h {
      height: 26px; display: flex; align-items: center;
      font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em;
      font-weight: 600; color: var(--muted);
    }
    /* A group header is not a section - it labels the sections under it, so it
       carries no top border of its own and sits tighter to what follows. */
    .rail-group { padding: 14px 14px 0; }
    .rail-group + .rail-section { border-top: none; }
    .group-head { display: flex; align-items: center; justify-content: space-between; }
    .group-title {
      font: 900 10px var(--sans); letter-spacing: .09em; text-transform: uppercase;
      color: #51677c;
    }
    .reset-link {
      border: 0; background: transparent; padding: 0; cursor: pointer;
      font: 700 10px var(--sans); color: var(--blue, #1565C0);
    }
    .reset-link:hover { text-decoration: underline; }

    .radio-list { display: flex; flex-direction: column; gap: 5px; margin-top: 3px; }
    .choice {
      display: flex; align-items: center; gap: 7px; padding: 2px 0;
      border: 0; background: none; cursor: pointer; text-align: left;
      font: 11.5px var(--sans); color: var(--text2);
    }
    .choice .dot {
      width: 12px; height: 12px; border-radius: 50%; flex: none;
      border: 1px solid #aebdca; background: #fff; position: relative;
    }
    .choice.on .dot { border-color: var(--blue, #1565C0); }
    .choice.on .dot::after {
      content: ''; position: absolute; inset: 2px; border-radius: 50%;
      background: var(--blue, #1565C0);
    }
    .choice.on { color: var(--text); font-weight: 600; }
    .choice:hover .dot { border-color: var(--blue, #1565C0); }

    .sel {
      width: 100%; margin-top: 4px; height: 24px; padding: 0 4px;
      border: 1px solid var(--border-light); border-radius: 3px;
      background: var(--panel); color: var(--text2);
      font: 11px var(--sans); cursor: pointer;
    }
    .sel:hover { border-color: var(--border); }
    .clear {
      margin-top: 4px; height: 20px; padding: 0 8px;
      border: 1px solid var(--border-light); border-radius: 3px;
      background: var(--panel); color: var(--label);
      font: 600 10.5px var(--sans); cursor: pointer;
    }
    .clear:hover { background: var(--bg); color: var(--navy); }
    .pills { display: flex; gap: 4px; flex-wrap: wrap; margin-top: 2px; }
    .pill {
      height: 24px; padding: 0 10px; border-radius: 12px;
      background: var(--panel); border: 1px solid var(--border-light);
      font-family: inherit; font-size: 11.5px; font-weight: 600; color: var(--text2);
      cursor: pointer;
    }
    .pill:hover { background: var(--bg); }
    .pill.on { background: var(--navy); border-color: var(--navy); color: #fff; }

    .cats { display: flex; flex-direction: column; gap: 2px; margin-top: 2px; }
    .cat {
      display: flex; align-items: center; justify-content: space-between;
      padding: 7px 10px; border-radius: 4px;
      font-size: 12.5px; font-weight: 600; color: var(--muted);
      cursor: pointer; border-left: 3px solid transparent;
    }
    .cat:hover { background: var(--bg); }
    .cat.on { background: var(--panel2); border-left-color: var(--navy); color: var(--navy); }
    .cat .ct { font-family: var(--mono); font-size: 10px; font-weight: 700; }
    .cat.locked { cursor: default; color: var(--muted-soft); background: var(--panel2); }
    .cat.locked:hover { background: var(--panel2); }
    /* Matches the row star exactly — one mark, one meaning. */
    .cat .star { color: #D4A017; }
    .cat.on .star { color: #D4A017; }

    .stat { font-size: 12.5px; color: var(--text2); margin-top: 4px; }
    .stat .v { font-family: var(--mono); font-size: 16px; font-weight: 700; color: var(--navy); }
    .stat.sub { font-size: 11px; color: var(--muted); margin-top: 3px; }

    .rail-btn {
      display: block; width: 100%; margin-top: 2px;
      background: var(--panel); border: 1px solid var(--border); border-radius: 4px;
      padding: 8px 10px; font-family: inherit; font-size: 11.5px; font-weight: 600;
      color: var(--text2); cursor: pointer; text-align: center;
    }
    .rail-btn:hover { background: var(--panel2); color: var(--navy); }
    .note { font-size: 10.5px; color: var(--muted); line-height: 1.45; margin-top: 6px; }

    .rail-section.dev { background: #FFFBF0; }
    .dev-tag {
      margin-left: 6px; font-size: 8.5px; font-weight: 900; letter-spacing: .4px;
      background: #FFF3CD; color: #7A5B00; border-radius: 3px; padding: 1px 4px;
    }
  `],
})
export class ChangesRailComponent {
  readonly svc = inject(RateChangesService);
  readonly session = inject(SessionContextService);
  private readonly router = inject(Router);

  readonly devViews = DEV_VIEW_MODES;

  readonly isGrid2 = computed(() => this.svc.viewMode() === 'grid2');
  readonly isCalendar = computed(() => this.svc.viewMode() === 'calendar');

  /** What the two shipping arrangements are for. Describes the ACTIVE one, so
   *  the note reads as a description rather than as an unclaimed offer. */
  readonly arrangementNote = computed(() => this.isCalendar()
    ? 'Month, week and day. The month shows shape — clusters, quiet weeks, which weekday a carrier favours; week and day open the carriers, and a product there opens the grid on that date.'
    : 'Date-led, newest first: every rate row with its % change and the carrier sheet in force. Switch to Calendar for the shape of a month.');

  readonly zones = [
    { key: 'future' as const, label: 'Future' },
    { key: 'today'  as const, label: 'Today'  },
    { key: 'past'   as const, label: 'Past'   },
  ];

  /** Reads a select's value off the change event. A helper rather than
   *  $any($event.target).value inline, which the template compiler accepts but
   *  which puts an untyped cast in four places. */
  asValue(e: Event): string {
    return (e.target as HTMLSelectElement | null)?.value ?? '';
  }

  /** Changing carrier clears the product: the old product almost never belongs
   *  to the new carrier, and leaving it set empties the grid with no visible
   *  cause. */
  pickCompany(v: string): void {
    this.svc.companyFilter.set(v);
    this.svc.productFilter.set('');
  }

  clearFilters(): void {
    this.svc.companyFilter.set('');
    this.svc.productFilter.set('');
  }
  readonly windows = [30, 90, 365];
  readonly pastWindows = [30, 90, 365];
  readonly types: ReadonlyArray<{ key: ChangeTabKey; label: string }> =
    CHANGE_TAB_KEYS.map(k => ({ key: k, label: k }));

  count(t: ChangeTabKey): number {
    return this.svc.byType()[t] ?? 0;
  }

  /** What each DEV view is for, so the choice is not three unexplained words.
   *  Grid and Calendar answer through arrangementNote() instead: they are no
   *  longer in this block, so a note describing one while no pill here is lit
   *  would point at nothing. */
  readonly viewNote = computed(() => {
    switch (this.svc.viewMode()) {
      case 'grid':     return 'The original grid, unchanged: one row per rate row, sortable columns, the basket, and the forward window. Was the landing view until 2026-08-26.';
      case 'company':  return 'Grouped by carrier and product, spanning the NOW line.';
      case 'timeline': return 'Grouped by the day a change takes effect, scheduled above the NOW line.';
      default:         return 'None of these is showing — the view above is. Each reads the same rows a different way.';
    }
  });

  readonly soonest = computed(() => {
    // Forward only. The service returns the past half in the same array now,
    // and min(days_until) across both is always negative — which rendered as
    // "The first lands today" on every load.
    const rows = this.svc.upcoming();
    if (!rows.length) return 'Nothing scheduled';
    const d = Math.min(...rows.map(r => r.days_until));
    return d <= 0 ? 'The first lands today' : `The first lands in ${d} days`;
  });

  goToRates(): void { void this.router.navigate(['/rates']); }
}
